<template>
  <client-only>
    <section
      class="navbar-first-search"
      :class="{ 'is-loading': isLoadingResults }"
    >
      <form class="field is-grouped" @submit.prevent="onFormSubmit">
        <div class="control is-expanded">
          <input
            ref="input"
            v-model="searchTerm"
            class="input"
            :placeholder="$texts('enterSearchTerm', 'Enter a search term')"
            @blur="onFormSubmit"
          />
        </div>
        <div v-if="!isUntilDesktop" class="control">
          <button
            class="button is-primary"
            :class="{ 'is-loading': isLoadingResults && searchTerm }"
          >
            <span class="icon">
              <SpriteSymbol name="search" class="size-4" />
            </span>
            <span>{{ $texts('search', 'Search') }}</span>
          </button>
        </div>
      </form>
      <div v-if="searchNotAvailable" class="message is-danger">
        <div class="message-body">
          {{
            $texts(
              'searchNotAvailable',
              'The search is currently not available.',
            )
          }}
        </div>
      </div>
      <div
        class="navbar-search-results"
        :class="{ 'is-loading': isLoadingResults && searchTerm }"
      >
        <ul>
          <li v-for="(item, index) in results" :key="item.nid">
            <StorageMenuOldNavigationNavbarFirstLevelSearchItem
              v-bind="item"
              @track="onItemClick(item, index)"
            />
          </li>
        </ul>
      </div>
    </section>
  </client-only>
</template>

<script lang="ts" setup>
import { getFilteredSearchText } from '~~/layers/search/app/helpers/search'
import { DomainId } from '#graphql-operations'
import { useDebounceFn } from '@vueuse/core'
import type { SearchHitContent } from '~~/layers/search/server/api/search/fulltext'
import { inject } from 'vue'

const { $texts } = useEasyTexts()
const { isUntilDesktop } = useViewport()
const { activeDomain } = useSiteContext()
const language = useCurrentLanguage()
const searchNotAvailable = ref(false)

const searchTerm = ref('')
const resultSearchTerm = ref('')
const isLoadingResults = ref(false)
const hasError = ref(false)
const results = ref<SearchHitContent[]>([])

const { trackEvent, getAnalyticsRouteName } = useAnalytics()

const searchTermFiltered = computed(() =>
  getFilteredSearchText(searchTerm.value),
)

async function fetchResults(
  text: string,
): Promise<{ error?: boolean; hits: SearchHitContent[] }> {
  if (!text) {
    return {
      hits: [],
      error: false,
    }
  }

  return $fetch('/api/search/fulltext', {
    query: {
      langcode: language.value,
      domain: activeDomain,
      text,
    },
  }).catch(() => {
    searchNotAvailable.value = true
    return {
      hits: [],
      error: true,
    }
  })
}

const updateFlyoutHeight = inject('updateFlyoutHeight') as
  (() => void) | undefined

const loadResults = useDebounceFn(async () => {
  isLoadingResults.value = true
  const text = searchTermFiltered.value
  const response = await fetchResults(text)
  results.value = response.hits
  resultSearchTerm.value = text
  isLoadingResults.value = false
  hasError.value = !!response.error

  if (updateFlyoutHeight) {
    updateFlyoutHeight()
  }

  trackEvent({
    event: 'viewSearchResults',
    searchTerm: text,
    searchResultsCount: results.value.length,
  })
}, 500)

function onFormSubmit() {
  if (activeDomain === DomainId.zuerich_com || !searchTermFiltered.value) {
    return
  }
}
watch(searchTermFiltered, loadResults)

function onItemClick(item: SearchHitContent, index: number) {
  trackEvent({
    event: 'clickSearchResult',
    searchTerm: searchTermFiltered.value,
    searchResultsCount: results.value.length,
    searchResultClickedPageLocation: document.location.origin + item.url,
    searchResultClickedPageTitle: item.title,
    searchResultClickedRouteName: getAnalyticsRouteName(),
    searchResultClickedIndex: index,
    searchResultClickedNodeID: item.nid,
  })
}
</script>

<style lang="postcss">
.navbar-first-search {
  @screen desktop {
    max-height: calc(100vh - (calc(9rem - 3px)) - 8rem);
    overflow: auto;
  }
  &.is-loading {
    cursor: loading;
  }

  form {
    .button,
    .input {
      @apply desktop:text-size-5;
    }

    .button {
      @apply h-full;
      padding: calc(0.5em - 2px) 1em;
    }

    .input {
      @apply font-normal text-button-color;
    }
  }
}

.navbar-search-results {
  &.is-loading {
    opacity: 0.3;
  }
}
</style>
